import { history } from 'umi';

import { buildLoginUrl } from '@/utils/infrastructure/utilities';

import { apiRoute } from '@/services/api';
import { authLogin, authRefreshToken } from '@/services/login';
import { queryCurrent } from '@/services/user';
import AppAuthMode from '@/utils/domain/constants/enumerator/AppAuthMode';
import AppKind from '@/utils/domain/constants/enumerator/AppKind';
import {
  clearSession,
  dropAppSessionOnLogout,
  findAppSessionUserId,
  findAutomyUserId,
  getCurrentSession,
  getItem,
  getTabItem,
  isStoredTokenFresh,
  resolveAutomySession,
  removeItem,
  removeTabItem,
  SESSION_TYPES,
  setCurrentSession,
  setItem,
  setTabItem,
  shouldReuseExistingAppToken,
  STORAGE_KEYS,
} from '@/utils/infrastructure/auth';

const AUTOMY_REFRESH_CONTEXT = { appId: null, portal: null };

const goToAppTwoFactor = (appId) => {
  history.replace(
    buildLoginUrl({
      pathname: '/user/login/two-factor',
      appId: String(appId),
      redirect: `${window.location.origin}/apps/${appId}`,
    }),
  );
};

const getStoredConfig = (userId = null, appId = null) => {
  try {
    if (userId && appId) {
      const stored = getItem(SESSION_TYPES.APP, userId, appId, STORAGE_KEYS.APP_CONFIG);
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const matchesAppId = (storedConfig, appId) => {
  if (!storedConfig || !appId) return false;
  return storedConfig.id === appId || storedConfig.appId === appId;
};

const isTokenStillValid = (tokenRaw) => isStoredTokenFresh(tokenRaw);

const isNetworkLikeError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('network') ||
    message.includes('offline') ||
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('request timeout')
  );
};

const initialState = {
  app: null,
  token: null,
  retryTimes: 0,
  authError: null,
};

// Guard against concurrent/rapid `auth` runs for the same app. AppGateway can
// mount/re-render several times during the (multi-second) refresh + authLogin
// window; without this, every run fires its own `POST /auth/app` (one gets
// canceled by HttpService dedup, the rest are wasted) and re-enters the refresh
// flow. Keyed by appId so switching between apps is never blocked.
let authInFlightAppId = null;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Automy refresh starts ~5 min before expiry, so the JWT still looks "valid".
 * Exchanging that soon-to-be-rotated token for an APP JWT 401s and AppGateway
 * bounces to the app login. Wait out the in-flight refresh, then re-read.
 */
const waitForAutomyRefresh = (userId) =>
  new Promise((resolve) => {
    if (!userId) {
      resolve(false);
      return;
    }
    const flag = getItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_REFRESHING);
    const startedAt = flag ? parseInt(flag, 10) : 0;
    if (!startedAt || Date.now() - startedAt >= 30_000) {
      resolve(false);
      return;
    }
    const deadline = Date.now() + 8_000;
    const tick = () => {
      const still = getItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_REFRESHING);
      if (!still || Date.now() >= deadline) {
        resolve(true);
        return;
      }
      setTimeout(tick, 200);
    };
    tick();
  });

const waitForInFlightAuth = (appId) =>
  new Promise((resolve) => {
    const deadline = Date.now() + 30_000;
    const tick = () => {
      if (authInFlightAppId !== appId || Date.now() >= deadline) {
        resolve();
        return;
      }
      setTimeout(tick, 150);
    };
    tick();
  });

const Model = {
  namespace: 'appAuth',
  state: initialState,
  effects: {
    *auth({ payload }, { call, put, select }) {
      const requestedAppId = payload?.appId || null;
      // Do not finish this effect while a twin is running: dva would flip
      // authLoading to false and AppGateway would treat leftover `app` as
      // "auth done" and send the user to the app login.
      if (requestedAppId && authInFlightAppId === requestedAppId) {
        yield call(waitForInFlightAuth, requestedAppId);
        return;
      }
      authInFlightAppId = requestedAppId;
      try {
        yield put({ type: 'beginAuth' });

        let { appId, versionId } = payload || {};

        // Get userId from current automy session
        const currentSession = getCurrentSession();
        let userId = currentSession?.userId;

        if (!appId) {
          const storedConfig = getStoredConfig(userId, null);
          if (storedConfig) appId = appId || storedConfig.id;
        }

        if (!appId) {
          console.warn('App auth aborted: missing appId');
          const retryTimes = yield select((o) => o.appAuth.retryTimes);
          if (!retryTimes || retryTimes < 3) {
            yield put({ type: 'queryRetry', payload: 1 });
          }
          return;
        }

        const currentApp = yield select((o) => o.appAuth.app);
        if (currentApp && currentApp.id !== appId) {
          if (userId && currentApp.appId) {
            removeItem(SESSION_TYPES.APP, userId, currentApp.appId, STORAGE_KEYS.APP_CONFIG);
          }
        }

        if (!userId && appId) {
          const fromAppToken = findAppSessionUserId(appId);
          if (fromAppToken) {
            const existingAppToken = getItem(
              SESSION_TYPES.APP,
              fromAppToken,
              String(appId),
              STORAGE_KEYS.AUTH_TOKEN,
            );
            if (existingAppToken && isTokenStillValid(existingAppToken)) {
              userId = fromAppToken;
              setCurrentSession({
                type: SESSION_TYPES.APP,
                userId: fromAppToken,
                resourceId: String(appId),
              });
            }
          }
        }

        if (!currentSession && !userId && appId) {
          const recoveredUserId = findAutomyUserId();
          if (recoveredUserId) {
            userId = recoveredUserId;

            const existingAppToken = getItem(
              SESSION_TYPES.APP,
              recoveredUserId,
              String(appId),
              STORAGE_KEYS.AUTH_TOKEN,
            );

            if (existingAppToken && isTokenStillValid(existingAppToken)) {
              setCurrentSession({
                type: SESSION_TYPES.APP,
                userId: recoveredUserId,
                resourceId: String(appId),
              });
            } else {
              setCurrentSession({
                type: SESSION_TYPES.AUTOMY,
                userId: recoveredUserId,
                resourceId: null,
              });
            }
          } else {
            // No AUTOMY user — check for self-managed app token (stored with appId as userId)
            const selfManagedToken = getItem(
              SESSION_TYPES.APP,
              String(appId),
              String(appId),
              STORAGE_KEYS.AUTH_TOKEN,
            );
            if (selfManagedToken && isTokenStillValid(selfManagedToken)) {
              userId = String(appId);
              setCurrentSession({
                type: SESSION_TYPES.APP,
                userId: String(appId),
                resourceId: String(appId),
              });
            }
          }
        }

        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        if (isOffline) {
          const storedConfig = getStoredConfig();
          if (matchesAppId(storedConfig, appId)) {
            yield put({
              type: 'queryAuth',
              payload: { token: null, app: storedConfig, appId, versionId: storedConfig.versionId },
            });
          }
          return;
        }

        const cacheKey = `${STORAGE_KEYS.APP_AUTH_CONFIG_PREFIX}${appId}`;
        const existingTokenRaw =
          userId && getItem(SESSION_TYPES.APP, userId, String(appId), STORAGE_KEYS.AUTH_TOKEN);
        const hasAppToken = existingTokenRaw && isTokenStillValid(existingTokenRaw);

        let response;
        if (hasAppToken) {
          try {
            const cached = getTabItem(cacheKey);
            if (cached) response = JSON.parse(cached);
          } catch (e) {}
        }

        if (!response) {
          response = yield call(apiRoute, {
            method: 'post',
            route: 'auth/app',
            data: {
              username: appId,
              password: versionId || '',
            },
          });
          setTabItem(cacheKey, JSON.stringify(response));
        }

        if (AppKind.isSite(response.app?.kind)) {
          yield put({
            type: 'setAuthError',
            payload: {
              code: 'SITE_KIND',
              publicUrl: response.app?.publicUrl || null,
            },
          });
          return;
        }

        const appAuthMode = AppAuthMode.parse(response.app?.authMode) || AppAuthMode.Automy;

        // Self-managed apps: public access, use token from auth/app directly
        if (appAuthMode === AppAuthMode.SelfManaged) {
          const selfManagedUserId = userId || String(appId);
          if (response.token?.accessToken) {
            const tokenData = JSON.stringify({
              ...response.token,
              issuedAt: Date.now(),
            });
            setItem(
              SESSION_TYPES.APP,
              selfManagedUserId,
              String(appId),
              STORAGE_KEYS.AUTH_TOKEN,
              tokenData,
            );
          }
          setCurrentSession({
            type: SESSION_TYPES.APP,
            userId: selfManagedUserId,
            resourceId: String(appId),
          });
          yield put({
            type: 'queryAuth',
            payload: {
              token: response.token,
              app: response.app,
              appId,
              versionId,
              userId: selfManagedUserId,
            },
          });
          return;
        }

        const isSwitchingApp =
          !currentSession ||
          currentSession.type !== SESSION_TYPES.APP ||
          String(currentSession.resourceId) !== String(appId);

        if (isSwitchingApp && userId && appId) {
          const existingAppToken = getItem(
            SESSION_TYPES.APP,
            userId,
            String(appId),
            STORAGE_KEYS.AUTH_TOKEN,
          );

          if (shouldReuseExistingAppToken(userId, appId)) {
            setCurrentSession({
              type: SESSION_TYPES.APP,
              userId,
              resourceId: String(appId),
            });
          } else {
            // Clean up expired APP token so hasAppToken() won't return a false positive.
            // A still-fresh leftover that is older than the AUTOMY refresh is kept
            // until /auth/automy + appId overwrites it.
            if (existingAppToken && !isTokenStillValid(existingAppToken)) {
              removeItem(SESSION_TYPES.APP, userId, String(appId), STORAGE_KEYS.AUTH_TOKEN);
            }

            const waitedForRefresh = yield call(waitForAutomyRefresh, userId);

            const userTokenRaw = getItem(
              SESSION_TYPES.AUTOMY,
              userId,
              null,
              STORAGE_KEYS.AUTH_TOKEN,
            );

            let validAutomyToken = null;
            let justRefreshedInline = waitedForRefresh;

            if (userTokenRaw && isTokenStillValid(userTokenRaw)) {
              validAutomyToken = JSON.parse(userTokenRaw);
            } else if (userTokenRaw) {
              // AUTOMY token exists but expired — attempt inline refresh.
              // Covers suspended tabs where the proactive worker refresh didn't land in time,
              // or the user navigated to /apps before the background refresh completed.
              try {
                setItem(
                  SESSION_TYPES.AUTOMY,
                  userId,
                  null,
                  STORAGE_KEYS.AUTH_REFRESHING,
                  Date.now().toString(),
                );
                const expiredToken = JSON.parse(userTokenRaw);
                const refreshed = yield call(
                  authRefreshToken,
                  expiredToken,
                  AUTOMY_REFRESH_CONTEXT,
                );
                if (refreshed?.accessToken) {
                  setItem(
                    SESSION_TYPES.AUTOMY,
                    userId,
                    null,
                    STORAGE_KEYS.AUTH_TOKEN,
                    JSON.stringify({ ...refreshed, issuedAt: Date.now() }),
                  );
                  validAutomyToken = refreshed;
                  justRefreshedInline = true;
                }
              } catch (_refreshErr) {
                // Refresh token also expired — will fall through to login redirect
              } finally {
                removeItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_REFRESHING);
              }
            }

            if (validAutomyToken) {
              let authAutomyOk = false;

              // Attempt 1: use the token we already have
              if (justRefreshedInline) {
                yield call(delay, 500);
              }
              try {
                const authRes = yield call(authLogin, {
                  username: 'automy',
                  route: '/auth/automy',
                  password: validAutomyToken.accessToken,
                  headers: { appId: String(appId) },
                });
                if (authRes?.twoFactorRequired && !authRes.accessToken) {
                  yield put({
                    type: 'auth/changeLoginStatus',
                    payload: {
                      ...authRes,
                      username: 'automy',
                      password: validAutomyToken.accessToken,
                    },
                  });
                  goToAppTwoFactor(appId);
                  yield put({
                    type: 'queryAuth',
                    payload: { token: null, app: response.app, appId, versionId },
                  });
                  return;
                }
                if (authRes?.accessToken) {
                  const tokenData = JSON.stringify({ ...authRes, issuedAt: Date.now() });
                  setItem(
                    SESSION_TYPES.APP,
                    userId,
                    String(appId),
                    STORAGE_KEYS.AUTH_TOKEN,
                    tokenData,
                  );
                  setCurrentSession({ type: SESSION_TYPES.APP, userId, resourceId: String(appId) });
                  authAutomyOk = true;
                }
              } catch (firstErr) {
                // Attempt 2: the Automy JWT we sent may have been rotated mid-flight
                // by useTokenRefresh. Reuse the stored token if it changed; only
                // force-refresh when it is still the same credential the server rejected.
                if (!isNetworkLikeError(firstErr)) {
                  let weSetRefreshing = false;
                  try {
                    const curRaw = getItem(
                      SESSION_TYPES.AUTOMY,
                      userId,
                      null,
                      STORAGE_KEYS.AUTH_TOKEN,
                    );
                    if (curRaw) {
                      let retryToken = JSON.parse(curRaw);
                      const tokenRotated =
                        retryToken?.accessToken &&
                        retryToken.accessToken !== validAutomyToken.accessToken &&
                        isTokenStillValid(curRaw);

                      if (!tokenRotated) {
                        yield call(waitForAutomyRefresh, userId);
                        const afterWait = getItem(
                          SESSION_TYPES.AUTOMY,
                          userId,
                          null,
                          STORAGE_KEYS.AUTH_TOKEN,
                        );
                        const waitedToken = afterWait ? JSON.parse(afterWait) : null;
                        if (
                          waitedToken?.accessToken &&
                          waitedToken.accessToken !== validAutomyToken.accessToken &&
                          isTokenStillValid(afterWait)
                        ) {
                          retryToken = waitedToken;
                        } else {
                          weSetRefreshing = true;
                          setItem(
                            SESSION_TYPES.AUTOMY,
                            userId,
                            null,
                            STORAGE_KEYS.AUTH_REFRESHING,
                            Date.now().toString(),
                          );
                          const refreshed = yield call(
                            authRefreshToken,
                            retryToken,
                            AUTOMY_REFRESH_CONTEXT,
                          );
                          if (refreshed?.accessToken) {
                            retryToken = refreshed;
                            setItem(
                              SESSION_TYPES.AUTOMY,
                              userId,
                              null,
                              STORAGE_KEYS.AUTH_TOKEN,
                              JSON.stringify({ ...refreshed, issuedAt: Date.now() }),
                            );
                          } else {
                            retryToken = null;
                          }
                        }
                      }

                      if (retryToken?.accessToken) {
                        const retryRes = yield call(authLogin, {
                          username: 'automy',
                          route: '/auth/automy',
                          password: retryToken.accessToken,
                          headers: { appId: String(appId) },
                        });
                        if (retryRes?.twoFactorRequired && !retryRes.accessToken) {
                          yield put({
                            type: 'auth/changeLoginStatus',
                            payload: {
                              ...retryRes,
                              username: 'automy',
                              password: retryToken.accessToken,
                            },
                          });
                          goToAppTwoFactor(appId);
                          yield put({
                            type: 'queryAuth',
                            payload: { token: null, app: response.app, appId, versionId },
                          });
                          return;
                        }
                        if (retryRes?.accessToken) {
                          const tokenData = JSON.stringify({ ...retryRes, issuedAt: Date.now() });
                          setItem(
                            SESSION_TYPES.APP,
                            userId,
                            String(appId),
                            STORAGE_KEYS.AUTH_TOKEN,
                            tokenData,
                          );
                          setCurrentSession({
                            type: SESSION_TYPES.APP,
                            userId,
                            resourceId: String(appId),
                          });
                          authAutomyOk = true;
                        }
                      }
                    }
                  } catch (_retryErr) {
                    // Both refresh and retry failed
                  } finally {
                    if (weSetRefreshing) {
                      removeItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_REFRESHING);
                    }
                  }
                }

                if (!authAutomyOk) {
                  const {
                    isOperationalAccessBlockedResponse,
                  } = require('@/utils/infrastructure/auth');
                  const {
                    handleOperationalAccessBlock,
                  } = require('@/utils/infrastructure/communication/authUtils');

                  // App surface never receives restrictedToken; still show access-blocked UX.
                  if (isOperationalAccessBlockedResponse(firstErr)) {
                    yield call(handleOperationalAccessBlock, firstErr, {
                      fromRefresh: false,
                      surface: 'app',
                      source: 'app-auth',
                    });
                    // Do not setAuthError — AppGateway would bounce to /user/login after 1.5s.
                    return;
                  }

                  const errMsg =
                    firstErr?.data?.message || firstErr?.message || 'App authentication failed';
                  yield put({
                    type: 'queryAuth',
                    payload: { token: response.token, app: response.app, appId, versionId, userId },
                  });
                  yield put({ type: 'setAuthError', payload: errMsg });
                  return;
                }
              }
            } else {
              // No usable AUTOMY token to exchange for an APP token (token absent
              // or its refresh failed because the refresh token also expired).
              // Stop here with a clean auth error so AppGateway redirects to login
              // WITH the app context, instead of falling through to queryCurrent —
              // which would trigger a redundant 401 -> reactive refresh -> logout
              // race and a second redirect that drops the app/redirect context.
              yield put({
                type: 'queryAuth',
                payload: { token: response.token, app: response.app, appId, versionId, userId },
              });
              yield put({
                type: 'setAuthError',
                payload: 'App authentication failed: session expired',
              });
              return;
            }
          }
        }

        try {
          const userData = yield call(queryCurrent);
          if (userData) yield put({ type: 'user/saveCurrentUser', payload: userData });
        } catch (_) {
          /* non-critical — user info unavailable offline */
        }
        yield put({
          type: 'queryAuth',
          payload: { token: response.token, app: response.app, appId, versionId, userId },
        });
      } catch (e) {
        const storedConfig = getStoredConfig();
        if (matchesAppId(storedConfig, payload?.appId) && isNetworkLikeError(e)) {
          yield put({
            type: 'queryAuth',
            payload: {
              token: null,
              app: storedConfig,
              appId: payload?.appId,
              versionId: storedConfig.versionId,
            },
          });
          return;
        }

        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        if (isOffline) return;
        const retryTimes = yield select((o) => o.appAuth.retryTimes);
        if (!retryTimes || retryTimes < 3) {
          yield put({ type: 'queryRetry', payload: 1 });
        }
      } finally {
        if (authInFlightAppId === requestedAppId) authInFlightAppId = null;
      }
    },

    *logout({ payload }, { put, select }) {
      try {
        const currentApp = yield select((o) => o.appAuth.app);
        const currentSession = getCurrentSession();
        const userId = currentSession?.userId;
        const appId = currentApp?.appId || currentApp?.id;

        if (appId) {
          removeTabItem(`${STORAGE_KEYS.APP_AUTH_CONFIG_PREFIX}${appId}`);
        }

        if (appId) dropAppSessionOnLogout(appId);
        else if (userId) {
          clearSession(SESSION_TYPES.APP, userId, appId);
        }

        if (currentSession?.type === SESSION_TYPES.APP) {
          setCurrentSession(resolveAutomySession(currentSession));
        } else if (userId) {
          setCurrentSession({ type: SESSION_TYPES.AUTOMY, userId, resourceId: null });
        } else {
          setCurrentSession(null);
        }

        if (payload?.isFailedAuthRedirect || payload?.isUserLogout) {
          history.replace(
            buildLoginUrl({
              appId,
              redirect: appId ? `${window.location.origin}/apps/${appId}` : undefined,
            }),
          );
          yield put({ type: 'reset' });
          return;
        }

        yield put({ type: 'reset' });
      } catch (e) {
        yield put({ type: 'reset' });
      }
    },

    *cleanup(_, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    queryAuth(state, { payload }) {
      const { token, app, appId, versionId, userId } = payload;
      const loginDef = app?.definition?.login || {};
      const parsedAuthMode = AppAuthMode.parse(app?.authMode) || AppAuthMode.Automy;
      const selfManaged = parsedAuthMode === AppAuthMode.SelfManaged;
      const config = {
        ...app,
        appId,
        versionId,
        authMode: parsedAuthMode,
        selfManaged,
        loginMainColor: loginDef.loginMainColor || app?.loginMainColor,
        loginDisableUserAndPass: loginDef.loginDisableUserAndPass ?? app?.loginDisableUserAndPass,
        loginDisableOffice365: loginDef.loginDisableOffice365 ?? app?.loginDisableOffice365,
        loginDisableGoogle: loginDef.loginDisableGoogle ?? app?.loginDisableGoogle,
        loginDisableChangeToAutomy:
          loginDef.loginDisableChangeToAutomy ?? app?.loginDisableChangeToAutomy,
        loginLogoUrl: loginDef.loginLogo?.downloadUrl || app?.loginLogoUrl,
        loginSplashUrl: loginDef.loginSplash?.downloadUrl || app?.loginSplashUrl,
      };

      if (userId && appId) {
        setItem(SESSION_TYPES.APP, userId, appId, STORAGE_KEYS.APP_CONFIG, JSON.stringify(config));
      }

      return {
        ...state,
        retryTimes: 0,
        authError: null,
        app: config,
        token,
      };
    },

    queryRetry(state, { payload }) {
      return {
        ...state,
        retryTimes: (state.retryTimes || 0) + payload,
      };
    },

    setAuthError(state, { payload }) {
      return {
        ...state,
        authError: payload,
      };
    },

    beginAuth() {
      return { ...initialState };
    },

    reset(state) {
      const currentSession = getCurrentSession();
      if (currentSession?.type === SESSION_TYPES.APP) {
        setCurrentSession(resolveAutomySession(currentSession));
      }
      return initialState;
    },
  },
};

export default Model;
